import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '@store/hooks';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import { Form } from 'react-bootstrap';

import { GetGlDailyRatesList, UpdatGlDailyRates } from '@store/Finance/DailyRates/DailyRatesSlice';
import useLookupCurrency from '@hooks/Lookups/use-Lookup-Currency';
import useGetGlDailyRatesList from '@hooks/Finance/DailyRates/useGetGlDailyRatesList';
import { type DailyRatesModels } from '@store/Finance/DailyRates/DailyRatesModels';
import { DailyRatesSchema } from '../../../ValidationForm/validationSchema';

 

interface Props {
  ID?: string;
}

interface FormValues {
  id: string;
  fromCurrencyCode: string;
  toCurrencyCode: string;
  conversionStartDate: string;
  conversionEndDate: string | null;
  conversionRate: string;
}

interface CurrencyOption {
  currencyCode: string;
  description: string;
}

interface DailyRatesModels {
  id: string;
  fromCurrencyCode: string;
  toCurrencyCode: string;
  conversionStartDate: string;
  conversionEndDate: string | null;
  conversionRate: number;
}

const EditDailyRates: React.FC<Props> = ({ ID }) => {
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Currency } = useLookupCurrency();
  const { DailyRatesList } = useGetGlDailyRatesList();
  const [dailyRateById, setDailyRateById] = useState<DailyRatesModels | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    if (DailyRatesList && ID) {
      const rateById = DailyRatesList.find((f: DailyRatesModels) => f.id === ID);
      setDailyRateById(rateById || null);
      setIsLoading(false);
    }
  }, [DailyRatesList, ID]);

  const formik = useFormik<FormValues>({
    initialValues: {
      id: dailyRateById?.id || '',
      fromCurrencyCode: dailyRateById?.fromCurrencyCode || '',
      toCurrencyCode: dailyRateById?.toCurrencyCode || '',
      conversionStartDate: dailyRateById?.conversionStartDate
        ? new Date(dailyRateById.conversionStartDate).toISOString().split('T')[0]
        : '',
      conversionEndDate: dailyRateById?.conversionEndDate || null,
      conversionRate: dailyRateById?.conversionRate.toString() || ''
    },
    enableReinitialize: true,
    validationSchema: DailyRatesSchema(t),
    onSubmit: (values, { setSubmitting, resetForm }) => {
      dispatch(
        UpdatGlDailyRates({
          id: values.id,
          fromCurrencyCode: values.fromCurrencyCode,
          toCurrencyCode: values.toCurrencyCode,
          conversionStartDate: values.conversionStartDate,
          conversionEndDate: values.conversionEndDate,
          conversionRate: parseFloat(values.conversionRate)
        })
      )
        .unwrap()
        .then((res: any) => {
          if (res?.succeeded === true) {
            CustomAlert({ action: 'Edit' });
            document.getElementById('closeModalForm')?.click();
            resetForm();
            dispatch(GetGlDailyRatesList());
          } else {
            setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch((error: any) => {
          setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });

  // التحقق من أن العملتين مختلفتين
  const isSameCurrency =
    formik.values.fromCurrencyCode && formik.values.fromCurrencyCode === formik.values.toCurrencyCode;

  // الحصول على التاريخ الحالي لتحديد الحد الأدنى للتاريخ
  const getTodayDate = (): string => {
    return new Date().toISOString().split('T')[0];
  };

  // التحقق مما إذا كان السعر نشطاً حالياً
  const isRateCurrentlyActive = (): boolean => {
    if (!dailyRateById?.conversionStartDate) return false;

    const now = new Date();
    const startDate = new Date(dailyRateById.conversionStartDate);
    const endDate = dailyRateById.conversionEndDate ? new Date(dailyRateById.conversionEndDate) : null;

    return now >= startDate && (!endDate || now <= endDate);
  };

  // معالجة تغيير العملة المصدر
  const handleFromCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    formik.handleChange(e);
    if (e.target.value === formik.values.toCurrencyCode) {
      formik.setFieldValue('toCurrencyCode', '');
    }
  };

  // معالجة تغيير العملة الهدف
  const handleToCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    formik.handleChange(e);
    if (e.target.value === formik.values.fromCurrencyCode) {
      formik.setFieldValue('fromCurrencyCode', '');
    }
  };

  if (isLoading) {
    return (
      <div className='text-center py-4'>
        <div className='spinner-border text-theme' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
        <p className='mt-2 text-muted'>{t('Loading')}...</p>
      </div>
    );
  }

  if (!dailyRateById && !isLoading) {
    return (
      <div className='text-center py-4'>
        <i className='bi bi-exclamation-triangle fa-2x text-warning mb-3'></i>
        <h5 className='text-muted'>{t('DataNotFound')}</h5>
        <p className='text-muted'>{t('TheRequestedRateWasNotFound')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* معلومات السعر الحالي */}
      {/* {dailyRateById && (
        <div className="alert alert-info mb-4">
          <div className="d-flex align-items-center">
            <i className="bi bi-info-circle me-2"></i>
            <strong>{i18n.language === 'ar' ? 'معلومات السعر الحالي' : 'Current Rate Information'}</strong>
          </div>
          <hr />
          <div className="row">
            <div className="col-6">
              <small className="text-muted">{i18n.language === 'ar' ? 'الحالة:' : 'Status:'}</small>
              <br />
              <span className={`badge ${isRateCurrentlyActive() ? 'bg-success' : 'bg-secondary'}`}>
                {isRateCurrentlyActive() ? 
                  (i18n.language === 'ar' ? 'نشط' : 'Active') : 
                  (i18n.language === 'ar' ? 'منتهي' : 'Expired')
                }
              </span>
            </div>
            <div className="col-6">
              <small className="text-muted">{i18n.language === 'ar' ? 'تاريخ الإنشاء:' : 'Created Date:'}</small>
              <br />
              <span>{new Date().toLocaleDateString('en-US')}</span>
            </div>
          </div>
        </div>
      )} */}

      <Form onSubmit={formik.handleSubmit}>
        <div className='row'>
          {/* العملة المصدر */}
          <div className='col-xl-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>{i18n.language === 'ar' ? 'من العملة' : 'From Currency'}</label>
              <Form.Select
                className='form-select'
                name='fromCurrencyCode'
                onChange={handleFromCurrencyChange}
                value={formik.values.fromCurrencyCode}
                isInvalid={!!(formik.touched.fromCurrencyCode && formik.errors.fromCurrencyCode)}
                disabled={isRateCurrentlyActive()} // تعطيل إذا كان السعر نشطاً
              >
                <option value=''>{i18n.language === 'ar' ? 'إختر العملة المصدر' : 'Choose Source Currency'}</option>
                {Currency?.map((item: CurrencyOption) => (
                  <option
                    key={item.currencyCode}
                    value={item.currencyCode}
                    disabled={item.currencyCode === formik.values.toCurrencyCode}
                  >
                    {item.currencyCode} - {item.description}
                  </option>
                ))}
              </Form.Select>
              {isRateCurrentlyActive() && (
                <Form.Text className='text-warning'>
                  <i className='bi bi-exclamation-triangle me-1'></i>
                  {i18n.language === 'ar'
                    ? 'لا يمكن تعديل العملة المصدر للسعر النشط'
                    : 'Cannot edit source currency for active rate'}
                </Form.Text>
              )}
              <Form.Control.Feedback type='invalid'>{formik.errors.fromCurrencyCode}</Form.Control.Feedback>
            </div>
          </div>

          {/* العملة الهدف */}
          <div className='col-xl-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>{i18n.language === 'ar' ? 'إلى العملة' : 'To Currency'}</label>
              <Form.Select
                className='form-select'
                name='toCurrencyCode'
                onChange={handleToCurrencyChange}
                value={formik.values.toCurrencyCode}
                isInvalid={!!(formik.touched.toCurrencyCode && formik.errors.toCurrencyCode)}
                disabled={isRateCurrentlyActive()} // تعطيل إذا كان السعر نشطاً
              >
                <option value=''>{i18n.language === 'ar' ? 'إختر العملة الهدف' : 'Choose Target Currency'}</option>
                {Currency?.map((item: CurrencyOption) => (
                  <option
                    key={item.currencyCode}
                    value={item.currencyCode}
                    disabled={item.currencyCode === formik.values.fromCurrencyCode}
                  >
                    {item.currencyCode} - {item.description}
                  </option>
                ))}
              </Form.Select>
              {isRateCurrentlyActive() && (
                <Form.Text className='text-warning'>
                  <i className='bi bi-exclamation-triangle me-1'></i>
                  {i18n.language === 'ar'
                    ? 'لا يمكن تعديل العملة الهدف للسعر النشط'
                    : 'Cannot edit target currency for active rate'}
                </Form.Text>
              )}
              <Form.Control.Feedback type='invalid'>{formik.errors.toCurrencyCode}</Form.Control.Feedback>
            </div>
          </div>

          {/* سعر التحويل */}
          <div className='col-xl-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>
                {i18n.language === 'ar' ? 'سعر التحويل' : 'Conversion Rate'}
              </label>
              <Form.Control
                className='form-control'
                type='number'
                step='0.0001'
                min='0'
                autoComplete='off'
                placeholder={i18n.language === 'ar' ? 'أدخل سعر التحويل' : 'Enter conversion rate'}
                name='conversionRate'
                onChange={formik.handleChange}
                value={formik.values.conversionRate}
                isInvalid={!!(formik.touched.conversionRate && formik.errors.conversionRate)}
              />
              <Form.Text className='text-muted'>
                {i18n.language === 'ar' ? 'يجب أن يكون السعر أكبر من الصفر' : 'Rate must be greater than zero'}
              </Form.Text>
              <Form.Control.Feedback type='invalid'>{formik.errors.conversionRate}</Form.Control.Feedback>
            </div>
          </div>

          {/* تاريخ البدء */}
          <div className='col-xl-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>{i18n.language === 'ar' ? 'تاريخ البدء' : 'Start Date'}</label>
              <Form.Control
                className='form-control'
                type='date'
                min={getTodayDate()}
                name='conversionStartDate'
                onChange={formik.handleChange}
                value={formik.values.conversionStartDate}
                isInvalid={!!(formik.touched.conversionStartDate && formik.errors.conversionStartDate)}
                disabled={isRateCurrentlyActive()} // تعطيل إذا كان السعر نشطاً
              />
              {isRateCurrentlyActive() ? (
                <Form.Text className='text-warning'>
                  <i className='bi bi-exclamation-triangle me-1'></i>
                  {i18n.language === 'ar'
                    ? 'لا يمكن تعديل تاريخ البدء للسعر النشط'
                    : 'Cannot edit start date for active rate'}
                </Form.Text>
              ) : (
                <Form.Text className='text-muted'>
                  {i18n.language === 'ar' ? 'لا يمكن اختيار تاريخ ماضي' : 'Cannot select past date'}
                </Form.Text>
              )}
              <Form.Control.Feedback type='invalid'>{formik.errors.conversionStartDate}</Form.Control.Feedback>
            </div>
          </div>
        </div>

        {/* تحذير إذا كانت العملتان متشابهتين */}
        {isSameCurrency && (
          <div className='alert alert-warning mb-3'>
            <i className='bi bi-exclamation-triangle me-2'></i>
            {i18n.language === 'ar'
              ? 'لا يمكن اختيار نفس العملة للمصدر والهدف'
              : 'Cannot select the same currency for source and target'}
          </div>
        )}

        {/* أزرار الإجراءات */}
        <div className='text-center mt-4'>
          <div className='d-flex justify-content-between gap-3'>
            <button
              type='submit'
              className='btn btn-lg btn-theme mb-1 w-100 rounded-1'
              disabled={formik.isSubmitting || !formik.isValid || isSameCurrency}
            >
              {formik.isSubmitting ? (
                <>
                  <div className='spinner-border spinner-border-sm me-2'></div>
                  {i18n.language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                </>
              ) : (
                <>
                  <i className='fas fa-save me-2'></i>
                  {t('Save')}
                </>
              )}
            </button>

            <button
              type='button'
              className='btn btn-lg btn-outline-secondary mb-1 w-100 rounded-1'
              data-bs-dismiss='modal'
              id='closeModalForm'
              disabled={formik.isSubmitting}
            >
              <i className='fas fa-times me-2'></i>
              {t('Cancel')}
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default EditDailyRates;
